import { CommonModule } from '@angular/common';
import { Component, OnInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CalendarOptions } from '@fullcalendar/core';
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { toZonedTime } from 'date-fns-tz';
import { addHours } from 'date-fns';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DividerModule } from 'primeng/divider';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import moment from 'moment';

import { PropertyService } from '../properties/service/property.service';
import { TourService } from '../followups/tour.service';
import { BlackoutService } from '../properties/service/blackout.service';
import { AvailabilityApiService } from '../properties/availability/availability.service';
import { SharedService } from 'src/app/core/shared/shared.service';

@Component({
  selector: 'app-tenants-portal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    CardModule,
    TableModule,
    TagModule,
    ToastModule,
    ProgressSpinnerModule,
    ButtonModule,
    DropdownModule,
    DividerModule,
    InputSwitchModule,
    DialogModule,
    CalendarModule,
    ConfirmDialogModule,
    FullCalendarModule,
  ],
  templateUrl: './tenants-portal.component.html',
  styleUrl: './tenants-portal.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class TenantsPortalComponent implements OnInit {

  propertyId = '';
  upcomingCount = 0;
  completedCount = 0;
  blackoutCount = 0;
  upcomingTours: any[] = [];
  loading = true;
  hasFullDayBlackoutDates = new Set<string>(); 

  days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  form!: FormGroup;

  hasBothBlackoutDates = new Set<string>();
  toursMap: Record<string, any[]> = {};
  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: { left: 'prev,next', center: 'title', right: ''},
    height: 'auto',
    contentHeight: 'auto',
    events: [],
    dayMaxEvents: 2,
    moreLinkContent: (args) => ({ html: `<span class="more-slots-link">+${args.num} more</span>` }),
    // moreLinkClick: () => false as any,
    moreLinkClick: (info) => {
      const clicked = moment(info.date).format('MM/DD/YYYY');
      setTimeout(() => this.ngZone.run(() => {
        if (moment(clicked, 'MM/DD/YYYY').isBefore(moment(), 'day')) return;
        const blackoutsOnDate = this.blackouts.filter(b => b.date === clicked);
        if (blackoutsOnDate.some(b => !b.userId && b.isFullDay)) return;
        if (blackoutsOnDate.some(b => b.userId && b.isFullDay)) return;
        // this.openBlackoutDialog(clicked);
        this.cdr.detectChanges();
      }));
      return false as any;
    },
    dayCellDidMount: (info) => {
      info.el.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;

        const closeBtn = target.closest('.cal-bo-close') as HTMLElement | null;
        if (closeBtn) {
          e.stopPropagation();
          const id = closeBtn.dataset['id'];
          if (id) setTimeout(() => this.ngZone.run(() => { this.showDeleteBlackoutDialog(id); this.cdr.detectChanges(); }));
          return;
        }

        if (target.closest('.tour-dot') || target.closest('.tour-more-link')) return;

        const clickedInsideEvents = target.closest('.fc-daygrid-day-events');
        if (clickedInsideEvents) return;
        const clicked = moment(info.date).format('MM/DD/YYYY');
        if (moment(clicked, 'MM/DD/YYYY').isBefore(moment(), 'day')) return;
        const blackoutsOnDate = this.blackouts.filter(b => b.date === clicked);
        if (blackoutsOnDate.some(b => !b.userId && b.isFullDay)) return;
        if (blackoutsOnDate.some(b => b.userId && b.isFullDay)) return;
        // setTimeout(() => this.ngZone.run(() => { this.openBlackoutDialog(clicked); this.cdr.detectChanges(); }));
        this.ngZone.run(() => { this.openBlackoutDialog(clicked); this.cdr.detectChanges(); });
      });

      this.renderDotsInCell(info);

      const dateStr = moment(info.date).format('MM/DD/YYYY');
      const hasBoth = this.hasBothBlackoutDates.has(dateStr);
      const hasUpcomingTour = (this.toursMap[dateStr] || [])
        .some(t => (t.tourStatus || t.status || '').toLowerCase() !== 'completed');
      const hasFullDayBlackout = this.hasFullDayBlackoutDates.has(dateStr);
      const shouldShowAlert = hasBoth || (hasFullDayBlackout && hasUpcomingTour);

      if (shouldShowAlert) {
        info.el.classList.add('has-both-blackouts');

        const frame = info.el.querySelector('.fc-daygrid-day-frame') || info.el;
        if (!(frame as HTMLElement).querySelector('.both-blackout-alert-icon')) {
          const icon = document.createElement('div');
          icon.className = 'both-blackout-alert-icon';
          icon.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                fill="none" stroke="#ef4444" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          `;
          (frame as HTMLElement).style.position = 'relative';
          (frame as HTMLElement).appendChild(icon);
        }
      }
    },
    eventContent: (arg) => {
      if (arg.event.extendedProps['isBlackoutBg']) return { html: '' };
      const label   = arg.event.extendedProps['blackoutLabel'] ?? '';
      if (!label) return null as any;
      const isAdmin = arg.event.extendedProps['isAdmin'];
      const id      = arg.event.extendedProps['blackout']?._id ?? '';
      const close   = isAdmin ? '' : `<span class="cal-bo-close" data-id="${id}">&times;</span>`;
      return { html: `<span class="cal-blackout-label" data-tip="${label}" title="${label}"><span class="cal-blackout-text">${label}</span>${close}</span>` };
    },
  };
  selectedTour: any = null; 

  landlordDisabledDays = new Set<string>();
  landlordRange: Record<string, { min: Date; max: Date }> = {};
  // Actual landlord slots per day (lowercase key) — for multi-slot gap awareness
  landlordDaySlots: Record<string, { start: string; end: string }[]> = {};
  // Dropdown options (30-min steps) generated from landlord slots — keyed by day label
  landlordTimeOptions: Record<string, { label: string; value: string }[]> = {};
  expandedDays = new Set<string>();

  savingAvailability = false;

  blackouts: any[] = [];
  propTimeZone = 'America/Chicago';
  showBlackoutDialog  = false;
  selectedBlackoutDate: string | null = null;
  isFullDayBlackout   = false;
  blackoutStartTime: Date | null = null;
  blackoutEndTime: Date | null   = null;
  blackoutReasonText  = '';
  editingBlackoutId: string | null = null;
  showBlackoutDeleteDialog = false;
  blackoutIdToDelete: string | null = null;
  blackoutTypes = [
    { label: 'Full Day',       value: true  },
    { label: 'Specific Time',  value: false },
  ];
  minDate = new Date();

  constructor(
    private fb: FormBuilder,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private propertyService: PropertyService,
    private tourService: TourService,
    private blackoutService: BlackoutService,
    private availabilityService: AvailabilityApiService,
    private sharedService: SharedService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.buildForm();

    this.propertyService.getUserInfo().subscribe(user => {
      this.propertyId    = user.property_id;
      this.propTimeZone  = user.property?.timezone || 'America/Chicago';
      this.loadDashboardData();
    });

    this.sharedService.currentUser$.subscribe((user: any) => {
      const availability = user?.weeklyAvailability;
      if (!availability?.length) return;
      const isSystemDefault = availability.every(
        (d: any) => d.available === true &&
          d.slots?.length === 1 &&
          d.slots[0].start === '00:00' &&
          d.slots[0].end === '23:59'
      );
      if (!isSystemDefault) {
        this.patchFormFromSaved(availability);
      }
    });
  }
  private buildForm(): void {
    const group: any = {};
    this.days.forEach(day => {
      group[day] = this.fb.array([]);
      group[`${day}_available`] = [false];
    });
    this.form = this.fb.group(group);
  }

  getSlots(day: string): FormArray {
    return this.form.get(day) as FormArray;
  }

  isAvailable(day: string): boolean {
    return this.form.get(`${day}_available`)?.value ?? false;
  }

  private makeSlotGroup(start: string, end: string): FormGroup {
    return this.fb.group({ start: [start], end: [end] });
  }


  private patchFormFromSaved(availability: any[]): void {
    availability.forEach((d: any) => {
      if (!this.days.includes(d.day)) return;
      this.form.get(`${d.day}_available`)?.setValue(d.available ?? false);
      const arr = this.getSlots(d.day);
      arr.clear();
      (d.slots || []).forEach((s: any) => {
        arr.push(this.makeSlotGroup(s.start, s.end));
      });
    });
  }

  loadDashboardData(): void {
    forkJoin({
      dashboard: this.tourService.getPropertyDashboard(this.propertyId),
      blackouts:  this.blackoutService.getBlackouts(this.propertyId),
      propAvail:  this.availabilityService.get(this.propertyId),
    }).subscribe({
      next: ({ dashboard, blackouts, propAvail }) => {
        this.upcomingTours  = dashboard.upcomingTours;
        this.upcomingCount  = dashboard.upcomingCount;
        this.completedCount = dashboard.completedCount;
        this.blackouts      = blackouts;
        const todayStr = moment().format('MM/DD/YYYY');
        this.blackoutCount = blackouts.filter(b =>
          moment(b.date, 'MM/DD/YYYY').isSameOrAfter(moment(todayStr, 'MM/DD/YYYY'), 'day')
        ).length;
        this.buildLandlordRange(propAvail?.weeklySlots);
        this.buildCalendarEvents(dashboard.completedTours ?? [], blackouts);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load dashboard data.' });
      },
    });
  }

  private buildCalendarEvents(completedTours: any[] = [], blackouts: any[] = []): void {
    this.toursMap = {};
    // Upcoming tours (yellow)
    this.upcomingTours.forEach(tour => {
      if (!this.toursMap[tour.date]) this.toursMap[tour.date] = [];
      this.toursMap[tour.date].push(tour);
    });
    // Completed tours (green)
    completedTours.forEach(tour => {
      if (!this.toursMap[tour.date]) this.toursMap[tour.date] = [];
      this.toursMap[tour.date].push({ ...tour, tourStatus: 'completed', status: 'completed' });
    });

    // Build FullCalendar events from blackouts (read-only)
    const byDate: Record<string, any[]> = {};
    blackouts.forEach(b => {
      if (!byDate[b.date]) byDate[b.date] = [];
      byDate[b.date].push(b);
    });

    const blackoutEvents: any[] = [];

    Object.entries(byDate).forEach(([dateStr, list]) => {
      const [m, d, y] = dateStr.split('/').map(Number);
      const bgStart = new Date(Date.UTC(y, m - 1, d));
      const bgEnd   = new Date(Date.UTC(y, m - 1, d + 1));

      const hasAdmin  = list.some(b => !b.userId);
      const hasTenant = list.some(b =>  b.userId);
      const hasBoth   = hasAdmin && hasTenant;

      let bgClass = 'cal-blackout-bg';
      if (hasBoth)        bgClass = 'cal-blackout-bg--both';
      else if (hasTenant) bgClass = 'cal-blackout-bg--tenant';
      // One grey background per date
      blackoutEvents.push({
        id: `bo-bg-${dateStr}`,
        start: bgStart, end: bgEnd,
        allDay: true, display: 'background',
        classNames: [bgClass],
        extendedProps: { isBlackoutBg: true, hasBoth },
      });

      // Block label per blackout
      list.forEach(b => {
        const isAdmin = !b.userId;
        const label   = isAdmin
          ? (b.isFullDay ? 'Blocked by Admin (Full Day)' : `Blocked by Admin ${this.fmtBlackout(b)}`)
          : (b.isFullDay ? 'My Blackout (Full Day)'      : `My Blackout ${this.fmtBlackout(b)}`);
        blackoutEvents.push({
          id: `bo-${b._id}`,
          start: bgStart, end: bgEnd,
          allDay: true, display: 'block',
          classNames: ['cal-blackout-block', isAdmin ? 'cal-blackout-admin' : 'cal-blackout-tenant',
            ...(hasBoth ? ['cal-blackout-both'] : []), ],
          extendedProps: { blackoutLabel: label, blackout: b, isAdmin },
        });
      });

    });

    this.hasBothBlackoutDates = new Set(
      Object.entries(byDate)
        .filter(([, list]) => list.some(b => !b.userId) && list.some(b => b.userId))
        .map(([date]) => date)
    );
    this.hasFullDayBlackoutDates = new Set(
      Object.entries(byDate)
        .filter(([, list]) => list.some(b => b.isFullDay === true))
        .map(([date]) => date)
    );

    this.calendarOptions = { ...this.calendarOptions, events: blackoutEvents };
  }

  private fmtBlackout(b: any): string {
    if (!b.startTime || !b.endTime) return '';
    const fmt = (t: string) => moment(t, 'HH:mm').format('h:mm A');
    return `${fmt(b.startTime)} – ${fmt(b.endTime)}`;
  }

  private renderDotsInCell(info: any): void {
    const dateStr = moment(info.date).format('MM/DD/YYYY');
    const tours: any[] = this.toursMap[dateStr] || [];
    if (!tours.length) return;

    const container = document.createElement('div');
    container.className = 'tour-dots-row';
    container.style.position = 'relative';
    container.style.zIndex   = '10';

    tours.slice(0, 3).forEach((tour) => {
      const dot = document.createElement('span');
      const status = (tour.status || '').toLowerCase();
      const isCompleted = status === 'completed';
      dot.className = 'tour-dot ' + (isCompleted ? 'dot-completed' : 'dot-upcoming');
      dot.addEventListener('click', e => {
        e.stopPropagation();
        this.ngZone.run(() => {
        document.querySelectorAll('.tour-popup').forEach(p => p.remove());

        const popup = document.createElement('div');
        popup.className = 'tour-popup';

        // Get severity for styling the manual badge
        const severity = this.getStatusSeverity(tour.status);
        
        popup.innerHTML = `
          <div class="tp-type">
            <span class="tp-dot ${isCompleted ? 'dot-completed' : 'dot-upcoming'}"></span>
            ${isCompleted ? 'Completed Tour' : 'Upcoming Tour'}
          </div>
          <div class="tp-content-body">
            <div class="tp-row"><span class="tp-label">Customer:</span><span class="tp-value">${tour.customer_name}</span></div>
            <!-- email is removed
             <div class="tp-row"><span class="tp-label">Email:</span><span class="tp-value">${tour.customer_email || '—'}</span></div> -->
            <div class="tp-row"><span class="tp-label">Unit / Plan:</span><span class="tp-value">${tour.plan_name}</span></div>
            <div class="tp-row"><span class="tp-label">Date & Time:</span><span class="tp-value">${this.formatTourDateTime(tour.date, tour.time)}</span></div>
          </div>
        `;

        document.body.appendChild(popup);

        const dotRect = (e.target as HTMLElement).getBoundingClientRect();
        const margin = 8;
        const popupW = popup.offsetWidth || 320;
        const popupH = popup.offsetHeight || 160;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let left = dotRect.left + window.scrollX;
        let top  = dotRect.bottom + window.scrollY + margin;

        if (dotRect.left + popupW > vw) {
          left = window.scrollX + Math.max(8, vw - popupW - 8);
        }
        if (dotRect.bottom + margin + popupH > vh) {
          top = dotRect.top + window.scrollY - margin - popupH;
        }
        if (top < window.scrollY + 8) {
          top = window.scrollY + 8;
        }

        popup.style.position = 'absolute';
        popup.style.left     = `${left}px`;
        popup.style.top      = `${top}px`;
        popup.style.zIndex   = '99999';
        popup.style.maxWidth = `${Math.min(360, vw - 16)}px`;
        
        setTimeout(() => {
          const close = (ev: MouseEvent) => {
            if (!popup.contains(ev.target as Node)) {
              popup.remove();
              document.removeEventListener('click', close);
            }
          };
          document.addEventListener('click', close);
        }, 0);
      });
    });

      container.appendChild(dot);
    });

    if (tours.length > 3) {
      const more = document.createElement('span');
      more.className = 'tour-more-link';
      more.textContent = `${tours.length - 3}+ more`;
      more.addEventListener('click', e => {
        e.stopPropagation();
        container.querySelectorAll('.tour-dot-hidden')
          .forEach(d => d.classList.remove('tour-dot-hidden'));
        more.style.display = 'none';
      });
      container.appendChild(more);
    }

    const frame = info.el.querySelector('.fc-daygrid-day-frame') || info.el;
    frame.appendChild(container);
  }

  handleTourEventClick(info: any): void {
    this.selectedTour = info.event.extendedProps.tour;
    // this.showTourDialog = true;
  }

  private buildLandlordRange(weeklySlots: Record<string, any[]> | undefined): void {
    this.landlordDisabledDays.clear();
    this.landlordDaySlots = {};
    this.landlordTimeOptions = {};

    this.days.forEach(day => {
      const key = day.toLowerCase();
      const slots: { start: string; end: string }[] = weeklySlots?.[key] ?? [];

      if (slots.length === 0) {
        this.landlordDisabledDays.add(day);
        this.form.get(`${day}_available`)?.setValue(false);
        this.form.get(`${day}_available`)?.disable();
        this.expandedDays.delete(day);
      } else {
        const sorted = [...slots].sort((a, b) => a.start.localeCompare(b.start));
        this.landlordDaySlots[key] = sorted;
        this.landlordTimeOptions[day] = this.generateTimeOptions(sorted);
      }
    });
  }

  private generateTimeOptions(slots: { start: string; end: string }[]): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [];
    slots.forEach(slot => {
      let [h, m] = slot.start.split(':').map(Number);
      const [eh, em] = slot.end.split(':').map(Number);
      const endMin = eh * 60 + em;
      while (h * 60 + m <= endMin) {
        const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        if (!options.find(o => o.value === value)) {
          options.push({ value, label: this.toLabel(h, m) });
        }
        m += 30;
        if (m >= 60) { m -= 60; h++; }
      }
    });
    return options;
  }

  private toLabel(h: number, m: number): string {
    const period = h >= 12 ? 'PM' : 'AM';
    const dh = h % 12 === 0 ? 12 : h % 12;
    return `${dh}:${String(m).padStart(2, '0')} ${period}`;
  }


  getLandlordSlotsSummary(day: string): string {
    const slots = this.landlordDaySlots[day.toLowerCase()];
    if (!slots?.length) return '';
    return slots
      .map(s => `${this.toLabel(...(s.start.split(':').map(Number) as [number, number]))} – ${this.toLabel(...(s.end.split(':').map(Number) as [number, number]))}`)
      .join(', ');
  }
  getLandlordSlots(day: string): string[] {
    const slots = this.landlordDaySlots[day.toLowerCase()];
    if (!slots?.length) return [];
    return slots.map(s =>
      `${this.toLabel(...(s.start.split(':').map(Number) as [number, number]))} – ` +
      `${this.toLabel(...(s.end.split(':').map(Number) as [number, number]))}`
    );
  }

  isLandlordDisabled(day: string): boolean {
    return this.landlordDisabledDays.has(day);
  }

  toggleExpand(day: string): void {
    if (this.isLandlordDisabled(day)) return;
    this.expandedDays.has(day)
      ? this.expandedDays.delete(day)
      : this.expandedDays.add(day);
  }

  isExpanded(day: string): boolean {
    return this.expandedDays.has(day);
  }

  addSlot(day: string): void {
    const opts = this.landlordTimeOptions[day] ?? [];
    const start = opts[0]?.value ?? '09:00';
    const end   = opts[1]?.value ?? '09:30';
    this.getSlots(day).push(this.makeSlotGroup(start, end));
  }

  removeSlot(day: string, index: number): void {
    this.getSlots(day).removeAt(index);
  }


  private validateSlotRanges(): string | null {
    for (const day of this.days) {
      const landlordSlots = this.landlordDaySlots[day.toLowerCase()];
      const slots = this.getSlots(day).value as { start: string; end: string }[];
      if (!slots.length) continue;

      const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

      for (let i = 0; i < slots.length; i++) {
        const s      = slots[i];
        const sStart = toMin(s.start);
        const sEnd   = toMin(s.end);

        if (sEnd <= sStart) {
          return `${day}: end time must be after start time`;
        }

        // Slot must fall entirely within one of the landlord's slots
        if (landlordSlots?.length) {
          const withinSlot = landlordSlots.some(ls =>
            sStart >= toMin(ls.start) && sEnd <= toMin(ls.end)
          );
          if (!withinSlot) {
            const ranges = landlordSlots
              .map(ls => `${moment(ls.start, 'HH:mm').format('h:mm A')}–${moment(ls.end, 'HH:mm').format('h:mm A')}`)
              .join(', ');
            return `${day}: ${moment(s.start, 'HH:mm').format('h:mm A')}–${moment(s.end, 'HH:mm').format('h:mm A')} must be within (${ranges})`;
          }
        }

        // Check overlap against other tenant slots on the same day
        for (let k = i + 1; k < slots.length; k++) {
          const o      = slots[k];
          const oStart = toMin(o.start);
          const oEnd   = toMin(o.end);
          if (sStart < oEnd && oStart < sEnd) {
            return `${day}: overlapping slots — `
              + `${moment(s.start, 'HH:mm').format('h:mm A')}–${moment(s.end, 'HH:mm').format('h:mm A')} `
              + `conflicts with ${moment(o.start, 'HH:mm').format('h:mm A')}–${moment(o.end, 'HH:mm').format('h:mm A')}`;
          }
        }
      }
    }
    return null;
  }

  saveAvailability(): void {
    const error = this.validateSlotRanges();
    if (error) {
      this.messageService.add({ severity: 'error', summary: 'Invalid Time', detail: error });
      return;
    }

    this.savingAvailability = true;
    const weeklyHours = this.days.map(day => ({
      day,
      available: this.form.get(`${day}_available`)?.value ?? false,
      slots: this.getSlots(day).value as { start: string; end: string }[],
    }));

    this.propertyService.saveAvailability({ weeklyHours }).subscribe({
      next: () => {
        this.savingAvailability = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Availability saved successfully!' });
      },
      error: () => {
        this.savingAvailability = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save availability.' });
      },
    });
  }


  get recentUpcomingTours(): any[] {
    return [...this.upcomingTours]
      .sort((a, b) => moment(a.planned_date || `${a.date} ${a.time}`, ['YYYY-MM-DD HH:mm', 'MM/DD/YYYY HH:mm'])
        .diff(moment(b.planned_date || `${b.date} ${b.time}`, ['YYYY-MM-DD HH:mm', 'MM/DD/YYYY HH:mm'])))
      .slice(0, 4);
  }

  openBlackoutDialog(dateString: string): void {
    this.selectedBlackoutDate = dateString;
    this.editingBlackoutId    = null;
    this.isFullDayBlackout    = false;
    this.blackoutReasonText   = '';
    const base  = moment().tz ? moment().tz(this.propTimeZone) : moment();
    const start = new Date();
    start.setHours(base.hours(), base.minutes(), 0, 0);
    this.blackoutStartTime = this.roundToNearest30(start);
    const end = new Date(this.blackoutStartTime!.getTime() + 60 * 60 * 1000);
    this.blackoutEndTime = this.roundToNearest30(end);
    this.showBlackoutDialog = true;
  }

  saveBlackout(): void {
    if (!this.selectedBlackoutDate) return;
    const payload: any = {
      date:         this.selectedBlackoutDate,
      isFullDay:    this.isFullDayBlackout,
      reasonText:   this.blackoutReasonText,
      propTimeZone: this.propTimeZone,
    };
    if (!this.isFullDayBlackout) {
      const fmt = (d: Date | null) => d
        ? `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
        : '';
      payload.startTime = fmt(this.blackoutStartTime);
      payload.endTime   = fmt(this.blackoutEndTime);
    }
    const req$ = this.editingBlackoutId
      ? this.blackoutService.updateBlackout(this.editingBlackoutId, payload)
      : this.blackoutService.createBlackout(this.propertyId, payload);

    req$.subscribe({
      next: () => { this.showBlackoutDialog = false; this.refreshBlackouts(); },
      error: (err) => this.messageService.add({
        severity: 'error', summary: 'Error',
        detail: err?.error?.message || 'Failed to save blackout.', life: 5000,
      }),
    });
  }

  showDeleteBlackoutDialog(id: string): void {
    this.blackoutIdToDelete       = id;
    this.showBlackoutDeleteDialog = true;
  }

  cancelDeleteBlackout(): void {
    this.showBlackoutDeleteDialog = false;
    this.blackoutIdToDelete       = null;
  }

  confirmDeleteBlackout(): void {
    if (!this.blackoutIdToDelete) return;
    this.blackoutService.deleteBlackout(this.blackoutIdToDelete).subscribe({
      next: () => { this.showBlackoutDeleteDialog = false; this.showBlackoutDialog = false; this.refreshBlackouts(); },
      error: (err) => console.error('Error deleting blackout', err),
    });
    this.blackoutIdToDelete = null;
  }

  private refreshBlackouts(): void {
    this.blackoutService.getBlackouts(this.propertyId).subscribe({
      next: (blackouts) => {
        this.blackouts     = blackouts;
        const todayStr     = moment().format('MM/DD/YYYY');
        this.blackoutCount = blackouts.filter(b =>
          moment(b.date, 'MM/DD/YYYY').isSameOrAfter(moment(todayStr, 'MM/DD/YYYY'), 'day')
        ).length;
        // Reload the tour data to rebuild calendar
        this.tourService.getPropertyDashboard(this.propertyId).subscribe(dashboard => {
          this.buildCalendarEvents(dashboard.completedTours ?? [], blackouts);
        });
      },
    });
  }

  private roundToNearest30(date: Date): Date {
    const d   = new Date(date);
    const rem = d.getMinutes() % 30;
    if (rem === 0) return d;
    d.setMinutes(d.getMinutes() + (30 - rem));
    d.setSeconds(0, 0);
    return d;
  }

  formatTourDateTime(date: string, time: string): string {
    const parsed = moment.utc(`${date} ${time}`, ['MM/DD/YYYY HH:mm', 'MM/DD/YYYY h A', 'MM/DD/YYYY h:mm A']);
    if (!parsed.isValid()) return `${date} ${time}`;
    const inPropTZ = toZonedTime(parsed.toDate(), this.propTimeZone || 'America/Chicago');
    return moment(inPropTZ).format('MMM D, YYYY h:mm A');
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warning' | 'danger' | undefined {
    const map: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      completed: 'success', scheduled: 'info', Active: 'info',
      Pending: 'warning', in_progress: 'info', rescheduled: 'warning', cancelled: 'danger',
    };
    return map[status];
  }

  formatTourDate(date: string, time: string): string {
    const parsed = moment.utc(`${date} ${time}`, ['MM/DD/YYYY HH:mm', 'MM/DD/YYYY h A', 'MM/DD/YYYY h:mm A']);
    if (!parsed.isValid()) return moment(date, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('ddd, MMM D');
    const inPropTZ = toZonedTime(parsed.toDate(), this.propTimeZone || 'America/Chicago');
    return moment(inPropTZ).format('ddd, MMM D');
  }

  formatTourTime(date: string, time: string): string {
    const parsed = moment.utc(`${date} ${time}`, ['MM/DD/YYYY HH:mm', 'MM/DD/YYYY h A', 'MM/DD/YYYY h:mm A']);
    if (!parsed.isValid()) return moment(time, ['HH:mm', 'h A', 'h:mm A']).format('h:mm A');
    const inPropTZ = toZonedTime(parsed.toDate(), this.propTimeZone || 'America/Chicago');
    return moment(inPropTZ).format('h:mm A');
  }

  ngAfterViewInit(): void {
    let tip: HTMLElement | null = null;
    const removeTip = () => { tip?.remove(); tip = null; };

    document.addEventListener('click', (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      const label = el.closest('.cal-blackout-label') as HTMLElement | null;

      if (!label || el.closest('.cal-bo-close') || !el.closest('.cal-blackout-text')) return removeTip();

      const content = label.getAttribute('data-tip');
      if (!content) return;

      e.stopPropagation();
      removeTip();

      tip = Object.assign(document.createElement('div'), { className: 'cal-blackout-tooltip-popup', textContent: content });
      document.body.appendChild(tip);

      const { left, top, bottom } = label.getBoundingClientRect();
      const { offsetWidth: tw, offsetHeight: th } = tip;

      tip.style.top  = `${top - th - 6 < 8 ? bottom + 6 : top - th - 6}px`;
      tip.style.left = `${Math.max(8, Math.min(left, window.innerWidth - tw - 8))}px`;
    });

    document.addEventListener('scroll', removeTip, true);
  }
}
